import React, { useState } from "react";
import { Link } from "react-router-dom";
import ReservationCard from "./ReservationCard";
import TimeChangeModal from "./TimeChangeModal";
import QRCodePopup from "../qr/QRCodePopup";
import { ACTIVITIES, getActivityByTable } from "../../config/activities";
import { useCapacitySync } from "../../hooks/useCapacitySync";
import { PASSWORD_MIN_LENGTH } from "../reservations/ReservationForm";

const SYNC_DELAY_MS = 500;
const JSON_HEADERS = { "Content-Type": "application/json" };

const postJson = async (url, body) => {
  const response = await fetch(url, { method: "POST", headers: JSON_HEADERS, body: JSON.stringify(body) });
  const data = await response.json().catch(() => ({}));
  return { response, data };
};

const isSameReservation = (a, b) => a.id === b.id && a.table === b.table;

const buildSummary = (teamName, reservations) => {
  const perActivity = ACTIVITIES.map(
    (activity) => `${reservations.filter((r) => r.table === activity.table).length} × ${activity.shortName}`
  ).join(", ");
  return `Našli jsme ${reservations.length} rezervací pro tým „${teamName}“ (${perActivity}).`;
};

const toQrData = (reservation) => ({
  id: reservation.id,
  team_name: reservation.teamName,
  team_number: reservation.participantCount,
  time_slot: reservation.time_slot,
  password: reservation.password,
  table: reservation.table,
});

const ManageReservation = () => {
  const { isSyncing } = useCapacitySync(true, SYNC_DELAY_MS);
  const [teamName, setTeamName] = useState("");
  const [password, setPassword] = useState("");
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [hasSearched, setHasSearched] = useState(false);
  const [qrReservation, setQrReservation] = useState(null);
  const [timeChangeTarget, setTimeChangeTarget] = useState(null);
  const [availabilityByTable, setAvailabilityByTable] = useState({});

  const fetchAvailability = async () => {
    try {
      const responses = await Promise.all(ACTIVITIES.map((activity) => fetch(activity.api.availability)));
      if (responses.some((response) => !response.ok)) return;
      const payloads = await Promise.all(responses.map((response) => response.json()));
      setAvailabilityByTable(
        Object.fromEntries(
          ACTIVITIES.map((activity, index) => [
            activity.table,
            { availability: payloads[index].availability || {}, capacityInfo: payloads[index].capacity_info || {} },
          ])
        )
      );
    } catch (err) {
      console.error("Error fetching time availability:", err);
    }
  };

  const validateSearch = () => {
    if (!teamName.trim()) return "Prosím zadejte název týmu";
    if (!password.trim()) return "Prosím zadejte heslo";
    if (password.length < PASSWORD_MIN_LENGTH) return `Heslo musí mít alespoň ${PASSWORD_MIN_LENGTH} znaky`;
    return "";
  };

  const handleSearch = async (event) => {
    event.preventDefault();
    const validationError = validateSearch();
    if (validationError) {
      setError(validationError);
      return;
    }
    setLoading(true);
    setError("");
    setSuccess("");
    setHasSearched(true);
    try {
      const query = `?team_name=${encodeURIComponent(teamName)}&password=${encodeURIComponent(password)}`;
      const responses = await Promise.all(ACTIVITIES.map((activity) => fetch(`${activity.api.reservations}${query}`)));
      const payloads = await Promise.all(responses.map((response) => (response.ok ? response.json() : { reservations: [] })));
      const found = payloads.flatMap((data, index) =>
        (data.reservations || []).map((reservation) => ({ ...reservation, table: ACTIVITIES[index].table }))
      );
      setReservations(found);
      setSuccess(buildSummary(teamName, found));
      await fetchAvailability();
    } catch (err) {
      setError("Chyba při načítání rezervací. Zkuste to prosím znovu.");
    } finally {
      setLoading(false);
    }
  };

  const handleTimeChange = async (reservation, newTimeSlot) => {
    const activity = getActivityByTable(reservation.table);
    try {
      const { response, data } = await postJson(activity.api.timeChange, {
        reservationId: reservation.id,
        newTimeSlot,
        password,
      });
      if (!response.ok || !data.success) {
        setError(data.error || "Chyba při změně času rezervace");
        return;
      }
      setReservations((prev) =>
        prev.map((r) => (isSameReservation(r, reservation) ? { ...r, time_slot: newTimeSlot, timeDisplay: newTimeSlot } : r))
      );
      await fetchAvailability();
      setTimeChangeTarget(null);
      setSuccess("Čas rezervace byl úspěšně změněn");
      setError("");
    } catch (err) {
      setError("Chyba sítě. Zkuste to prosím znovu.");
    }
  };

  const handleCancel = async (reservation) => {
    if (!window.confirm("Opravdu chcete zrušit tuto rezervaci?")) return;
    const activity = getActivityByTable(reservation.table);
    try {
      const { response, data } = await postJson(activity.api.cancel, { reservationId: reservation.id, password });
      if (!response.ok) throw new Error(data.error || "Rezervaci se nepodařilo zrušit");
      setReservations((prev) =>
        prev.map((r) => (isSameReservation(r, reservation) ? { ...data.reservation, table: reservation.table } : r))
      );
      setSuccess("Rezervace byla úspěšně zrušena");
    } catch (err) {
      setError(err.message || "Chyba při rušení rezervace. Zkuste to prosím znovu.");
    }
  };

  const availabilityForTarget = timeChangeTarget ? availabilityByTable[timeChangeTarget.table] : undefined;

  return (
    <>
      <div className="rhead">
        <span className="kicker">Správa rezervací</span>
        <h1 className="display-h1">VAŠE MÍSTO NA OFRENDĚ</h1>
        <p className="lead">Zadejte název vašeho týmu a heslo pro zobrazení a správu vašich rezervací.</p>
      </div>

      {isSyncing && (
        <p className="msg info mx-auto mb-4 max-w-[820px]" role="status">
          <span className="spinner" />
          Synchronizuji kapacitu slotů…
        </p>
      )}

      <section className="card main mx-auto mb-14 max-w-[820px]">
        <h3>Vyhledat rezervaci</h3>
        <form onSubmit={handleSearch} className="grid gap-4 sm:grid-cols-2">
          <div className="field">
            <label htmlFor="manage-team">
              Název týmu <small>*</small>
            </label>
            <input
              id="manage-team"
              value={teamName}
              onChange={(event) => setTeamName(event.target.value)}
              required
              placeholder="Název týmu použitý při registraci"
            />
          </div>
          <div className="field">
            <label htmlFor="manage-password">
              Heslo <small>*</small>
            </label>
            <input
              id="manage-password"
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              required
              minLength={PASSWORD_MIN_LENGTH}
              placeholder="Heslo použité při registraci"
            />
          </div>
          <div className="sm:col-span-2">
            <button type="submit" className="btn btn-marigold w-full" disabled={loading}>
              {loading ? "Hledám v knize mrtvých…" : "Zobrazit rezervace"}
            </button>
          </div>
        </form>

        {error && (
          <p className="msg error" role="alert">
            {error}
          </p>
        )}
        {success && (
          <p className="msg ok" role="status">
            {success}
          </p>
        )}

        {reservations.length > 0 && (
          <div className="grid gap-4">
            <h3>Vaše rezervace</h3>
            {reservations.map((reservation) => (
              <ReservationCard
                key={`${reservation.table}-${reservation.id}`}
                reservation={reservation}
                onChangeTime={() => setTimeChangeTarget(reservation)}
                onShowQr={() => setQrReservation(toQrData(reservation))}
                onCancel={() => handleCancel(reservation)}
              />
            ))}
          </div>
        )}

        {reservations.length === 0 && hasSearched && !loading && (
          <div className="empty">
            <div className="big" aria-hidden="true">
              ☠
            </div>
            <h4 className="font-display text-xl text-marigold">Žádná rezervace nenalezena</h4>
            <p>
              Pro tým <b className="text-bone">{teamName}</b> nebyla nalezena žádná rezervace.
            </p>
          </div>
        )}

        <p className="already">
          Ještě nemáte rezervaci? <Link to="/reservations">Vytvořit novou</Link>
        </p>
      </section>

      <TimeChangeModal
        reservation={timeChangeTarget}
        availability={availabilityForTarget?.availability}
        capacityInfo={availabilityForTarget?.capacityInfo}
        onClose={() => setTimeChangeTarget(null)}
        onSelect={handleTimeChange}
      />
      <QRCodePopup isOpen={Boolean(qrReservation)} onClose={() => setQrReservation(null)} reservationData={qrReservation} />
    </>
  );
};

export default ManageReservation;
